import { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MaterialContext } from "../../context/MaterialContext";
import { authHeaders } from "../../utilities/AuthState";
import { buildApiUrl } from "../../utilities/Static";

const MAX_CSV_BYTES = 2 * 1024 * 1024;

function filenameFromDisposition(header, fallback) {
  const match = /filename="?([^"]+)"?/i.exec(header || "");
  return match?.[1] || fallback;
}

async function saveCsvFile(blob, filename) {
  if (typeof window.showSaveFilePicker === "function") {
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: "CSV",
          accept: { "text/csv": [".csv"] },
        },
      ],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function MaterialPrice() {
  const { refetch } = useContext(MaterialContext);
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownload = async () => {
    setBusy("download");
    setError("");
    setApplied(null);
    try {
      const response = await axios.get(buildApiUrl("/material/pricing/export"), {
        responseType: "blob",
        headers: authHeaders(),
      });
      const payload = response.data;
      const text = payload instanceof Blob ? await payload.text() : String(payload || "");
      const contentType = String(response.headers["content-type"] || "");
      const looksLikeJson =
        contentType.includes("json") || text.trimStart().startsWith("{");
      if (looksLikeJson) {
        let message = "This API does not support pricing export yet.";
        try {
          const parsed = JSON.parse(text);
          message =
            parsed.msg ||
            parsed.message ||
            message;
        } catch (parseError) {
          message = "This API does not support pricing export yet.";
        }
        setError(message);
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = filenameFromDisposition(
        response.headers["content-disposition"],
        `matlib-prices-${stamp}.csv`,
      );
      const blob = new Blob([text], {
        type: "text/csv;charset=utf-8",
      });
      await saveCsvFile(blob, filename);
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }
      let message = "Could not download the pricing CSV.";
      const payload = err.response?.data;
      if (payload instanceof Blob) {
        try {
          const parsed = JSON.parse(await payload.text());
          message = parsed.msg || parsed.message || message;
        } catch (parseError) {
          message = "Could not download the pricing CSV.";
        }
      } else if (payload?.msg || payload?.message) {
        message = payload.msg || payload.message;
      }
      setError(message);
    } finally {
      setBusy("");
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setError("That file is too large. Export a fresh CSV and try again.");
      resetFileInput();
      return;
    }

    setBusy("preview");
    setError("");
    setApplied(null);
    setPreview(null);
    setFileName(file.name);
    try {
      const csv = await file.text();
      const { data } = await axios.post(
        buildApiUrl("/material/pricing/preview"),
        { csv },
        { headers: authHeaders() },
      );
      setPreview(data);
    } catch (err) {
      setError(
        err.response?.data?.msg ||
          err.response?.data?.message ||
          "Could not read that CSV.",
      );
    } finally {
      setBusy("");
      resetFileInput();
    }
  };

  const handleApply = async () => {
    const changes = preview?.changes || [];
    if (!changes.length) {
      return;
    }
    const confirmed = window.confirm(
      `Apply ${changes.length} price change${changes.length === 1 ? "" : "s"} to the catalog?`,
    );
    if (!confirmed) {
      return;
    }

    setBusy("apply");
    setError("");
    try {
      const { data } = await axios.post(
        buildApiUrl("/material/pricing/import"),
        {
          rows: changes.map((row) => ({
            id: row.id,
            vendor: row.vendor,
            price: row.to,
          })),
        },
        { headers: authHeaders() },
      );
      setApplied(data);
      setPreview(null);
      setFileName("");
      if (typeof refetch === "function") {
        await refetch();
      }
    } catch (err) {
      setError(
        err.response?.data?.msg ||
          err.response?.data?.message ||
          "Could not apply those prices.",
      );
    } finally {
      setBusy("");
    }
  };

  const changeCount = preview?.changes?.length || 0;
  const errorCount = preview?.errors?.length || 0;

  return (
    <div className="content pricingPage">
      <div className="pricingSheet">
        <header className="pricingIntro">
          <h2>Price Update</h2>
          <p>
            Download the catalog as a CSV, change only the <strong>price</strong>{" "}
            column in Excel, then upload it here. Matlib matches rows by{" "}
            <strong>id</strong> — leave id, vendor, slug, and title alone.
          </p>
          <p className="pricingHint">
            Generic Matlib prices only. One-off edits still live on{" "}
            <Link to="/material/add">Add/Edit Materials</Link>.
          </p>
        </header>

        <div className="pricingActions">
          <button
            className="button-highlight"
            type="button"
            onClick={handleDownload}
            disabled={Boolean(busy)}
          >
            {busy === "download" ? "Downloading…" : "Download CSV"}
          </button>
          <label className={busy ? "pricingFileLabel isDisabled" : "pricingFileLabel"}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              disabled={Boolean(busy)}
            />
            {busy === "preview" ? "Reading…" : "Upload CSV"}
          </label>
        </div>

        {error ? <p className="pricingError">{error}</p> : null}
        {applied ? (
          <p className="pricingSuccess">
            Applied {applied.updated} price
            {applied.updated === 1 ? "" : "s"}. Library prices are updated.
          </p>
        ) : null}

        {preview ? (
          <section className="pricingPreview">
            <div className="pricingSummary">
              <div>
                <strong>{changeCount}</strong> will change
              </div>
              <div>
                <strong>{preview.unchanged || 0}</strong> unchanged
              </div>
              <div>
                <strong>{errorCount}</strong> error{errorCount === 1 ? "" : "s"}
              </div>
              {fileName ? <div className="pricingFileName">{fileName}</div> : null}
            </div>

            <div className="pricingPreviewActions">
              <button
                className="button-highlight"
                type="button"
                onClick={handleApply}
                disabled={!changeCount || Boolean(busy)}
              >
                {busy === "apply"
                  ? "Applying…"
                  : `Apply ${changeCount} change${changeCount === 1 ? "" : "s"}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  setFileName("");
                }}
                disabled={Boolean(busy)}
              >
                Clear
              </button>
            </div>

            {errorCount > 0 ? (
              <ul className="pricingErrors">
                {preview.errors.map((item) => (
                  <li key={`${item.row}-${item.id}-${item.message}`}>
                    Row {item.row}
                    {item.id ? ` · id ${item.id}` : ""}: {item.message}
                  </li>
                ))}
              </ul>
            ) : null}

            {changeCount > 0 ? (
              <div className="table-area">
                <table className="table">
                  <thead>
                    <tr className="table-head">
                      <th>Vendor</th>
                      <th>Title</th>
                      <th>Slug</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changes.map((row) => (
                      <tr key={row.id}>
                        <td>{row.vendor}</td>
                        <td>
                          <Link
                            to={`/material/edit/${row.vendor}/${row.slug}`}
                          >
                            {row.title}
                          </Link>
                        </td>
                        <td>{row.slug}</td>
                        <td className="pricingFrom">{row.from}</td>
                        <td className="pricingTo">{row.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="pricingHint">
                No price changes in that file. Edit the price column and upload
                again.
              </p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default MaterialPrice;
